/**
 * Profile form — entirely optional for every role.
 *
 * Lives at /settings/profile (preferred entry) and /onboard/profile (legacy,
 * still works for old activation links and direct navigation). Same component
 * either way. Profile fields — contact, resume, work auth, professional,
 * personal, staffing — are all opt-in; nothing redirects anyone here. Admins
 * and employees both choose what to share. Tenant admins + super-admins can
 * view whatever's filled in via the Members → View profile drawer.
 *
 * Resume uploads use a presigned R2 PUT so the FastAPI process never sees
 * the bytes; we HEAD-check the object on confirm.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState, type CSSProperties } from "react";

import { api, extractErrorMessage } from "@/lib/api";
import type {
  EducationEntry,
  EmergencyContact,
  EmployeeProfile,
  ProfileUpdate,
} from "@/lib/types";
import { useAuthStore } from "@/store/auth";

const VISA_OPTIONS: { code: string; label: string }[] = [
  { code: "us_citizen", label: "US Citizen" },
  { code: "green_card", label: "Green Card / Permanent Resident" },
  { code: "h1b", label: "H1B" },
  { code: "h4_ead", label: "H4-EAD" },
  { code: "l2_ead", label: "L2-EAD" },
  { code: "opt", label: "F-1 OPT" },
  { code: "cpt", label: "F-1 CPT" },
  { code: "tn", label: "TN" },
  { code: "other", label: "Other" },
];

// Mirrors backend ALLOWED_RESUME_TYPES + ALLOWED_RESUME_EXTENSIONS. Browsers
// disagree on Word-doc MIME types (and Edge/Windows sometimes hands us
// "application/octet-stream" for a .docx), so we accept on either the MIME
// matching OR the filename ending with a known extension.
const ALLOWED_RESUME_TYPES = new Set([
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-word.document.macroEnabled.12",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.template",
  "application/vnd.ms-word.template.macroEnabled.12",
]);
const ALLOWED_RESUME_EXTENSIONS = [".pdf", ".doc", ".docx", ".docm", ".dotx", ".dotm"];

function isAllowedResume(file: File): boolean {
  if (ALLOWED_RESUME_TYPES.has(file.type)) return true;
  const lower = file.name.toLowerCase();
  return ALLOWED_RESUME_EXTENSIONS.some((ext) => lower.endsWith(ext));
}

export function OnboardProfilePage({ mode = "onboard" }: { mode?: "onboard" | "settings" }) {
  const setProfileComplete = useAuthStore((s) => s.setProfileComplete);
  const queryClient = useQueryClient();

  const { data: profile, isLoading } = useQuery({
    queryKey: ["my-profile"],
    queryFn: async () => (await api.get<EmployeeProfile>("/me/profile")).data,
  });

  // Required
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");

  // Optional
  const [visaStatus, setVisaStatus] = useState("");
  const [visaExpiry, setVisaExpiry] = useState("");
  const [linkedinUrl, setLinkedinUrl] = useState("");
  const [githubUrl, setGithubUrl] = useState("");
  const [skillsText, setSkillsText] = useState("");
  const [yearsExperience, setYearsExperience] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [emergencyName, setEmergencyName] = useState("");
  const [emergencyRelation, setEmergencyRelation] = useState("");
  const [emergencyPhone, setEmergencyPhone] = useState("");
  const [hourlyRate, setHourlyRate] = useState("");
  const [salaryExpectation, setSalaryExpectation] = useState("");
  const [noticeDays, setNoticeDays] = useState("");
  const [educationEntries, setEducationEntries] = useState<EducationEntry[]>([]);

  const [banner, setBanner] = useState<{ kind: "error" | "info"; text: string } | null>(
    null,
  );
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);
  const sigInputRef = useRef<HTMLInputElement | null>(null);
  const [sigBusy, setSigBusy] = useState(false);

  // Hydrate the form ONCE when the profile first loads. After that, local
  // state is authoritative until the user saves. Without this guard, every
  // cache update (e.g. the resume upload's setQueryData) re-runs this effect
  // and clobbers anything the user typed but hasn't saved yet.
  // Resume-only fields (filename, size, upload date) read directly from
  // `profile` in the render path below, so the resume chip still updates
  // live without touching form state.
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (!profile || hydratedRef.current) return;
    hydratedRef.current = true;
    setPhone(profile.phone ?? "");
    setAddress(profile.address ?? "");
    setVisaStatus(profile.visa_status ?? "");
    setVisaExpiry(profile.visa_expiry ?? "");
    setLinkedinUrl(profile.linkedin_url ?? "");
    setGithubUrl(profile.github_url ?? "");
    setSkillsText((profile.skills ?? []).join(", "));
    setYearsExperience(profile.years_experience?.toString() ?? "");
    setDateOfBirth(profile.date_of_birth ?? "");
    setEmergencyName(profile.emergency_contact?.name ?? "");
    setEmergencyRelation(profile.emergency_contact?.relation ?? "");
    setEmergencyPhone(profile.emergency_contact?.phone ?? "");
    setHourlyRate(profile.hourly_rate_usd ?? "");
    setSalaryExpectation(profile.salary_expectation_usd ?? "");
    setNoticeDays(profile.notice_period_days?.toString() ?? "");
    setEducationEntries(profile.education ?? []);
  }, [profile]);

  const save = useMutation({
    mutationFn: async (body: ProfileUpdate) =>
      (await api.put<EmployeeProfile>("/me/profile", body)).data,
    onSuccess: (data) => {
      queryClient.setQueryData(["my-profile"], data);
      setProfileComplete(data.is_complete);
      setBanner({ kind: "info", text: "Saved." });
    },
    onError: (err) => {
      setBanner({
        kind: "error",
        text: extractErrorMessage(err, "Couldn't save — try again."),
      });
    },
  });

  const onSubmit = () => {
    setBanner(null);
    if (!phone.trim() || !address.trim()) {
      setBanner({ kind: "error", text: "Phone and address are required." });
      return;
    }
    const body: ProfileUpdate = {
      phone: phone.trim(),
      address: address.trim(),
    };
    if (visaStatus) body.visa_status = visaStatus;
    if (visaExpiry) body.visa_expiry = visaExpiry;
    if (linkedinUrl.trim()) body.linkedin_url = linkedinUrl.trim();
    if (githubUrl.trim()) body.github_url = githubUrl.trim();
    const skills = skillsText
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    if (skills.length) body.skills = skills;
    if (yearsExperience) body.years_experience = Number(yearsExperience);
    if (dateOfBirth) body.date_of_birth = dateOfBirth;
    const ec: EmergencyContact | null =
      emergencyName.trim() && emergencyPhone.trim()
        ? {
            name: emergencyName.trim(),
            relation: emergencyRelation.trim() || "other",
            phone: emergencyPhone.trim(),
          }
        : null;
    if (ec) body.emergency_contact = ec;
    if (hourlyRate) body.hourly_rate_usd = hourlyRate;
    if (salaryExpectation) body.salary_expectation_usd = salaryExpectation;
    if (noticeDays) body.notice_period_days = Number(noticeDays);
    if (educationEntries.length) body.education = educationEntries;
    save.mutate(body);
  };

  const onResumeChosen = async (file: File) => {
    setBanner(null);
    if (!isAllowedResume(file)) {
      setBanner({
        kind: "error",
        text: "Resume must be a PDF or Word document (.pdf, .doc, or .docx).",
      });
      return;
    }
    if (file.size > 100 * 1024 * 1024) {
      setBanner({ kind: "error", text: "Resume must be 100 MB or smaller." });
      return;
    }
    setUploading(true);
    try {
      // Multipart POST → backend forwards to R2. Simpler than the
      // presigned-URL dance and doesn't require R2 CORS to be configured.
      // Don't set Content-Type manually — axios derives it from FormData
      // with the correct multipart boundary.
      const form = new FormData();
      form.append("file", file, file.name);
      const updated = (
        await api.post<EmployeeProfile>("/me/profile/resume", form)
      ).data;
      queryClient.setQueryData(["my-profile"], updated);
      setProfileComplete(updated.is_complete);
      setBanner({ kind: "info", text: "Resume uploaded." });
    } catch (err) {
      setBanner({
        kind: "error",
        text:
          err instanceof Error ? err.message : "Resume upload failed — try again.",
      });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const onSignatureChosen = async (file: File) => {
    // Validate client-side so the user gets a clean inline error instead of a
    // failed request. PNG / JPG / WebP, ≤ 2 MB.
    const okType =
      new Set(["image/png", "image/jpeg", "image/webp"]).has(file.type) ||
      /\.(png|jpe?g|webp)$/i.test(file.name);
    if (!okType) {
      setBanner({ kind: "error", text: "Signature must be a PNG, JPG, or WebP image." });
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setBanner({ kind: "error", text: "Signature must be 2 MB or smaller." });
      return;
    }
    setSigBusy(true);
    try {
      const form = new FormData();
      form.append("file", file, file.name);
      const updated = (await api.post<EmployeeProfile>("/me/profile/signature", form)).data;
      queryClient.setQueryData(["my-profile"], updated);
      setBanner({ kind: "info", text: "Signature uploaded." });
    } catch (err) {
      setBanner({
        kind: "error",
        text: err instanceof Error ? err.message : "Signature upload failed — try again.",
      });
    } finally {
      setSigBusy(false);
      if (sigInputRef.current) sigInputRef.current.value = "";
    }
  };

  const onSignatureRemove = async () => {
    setSigBusy(true);
    try {
      const updated = (await api.delete<EmployeeProfile>("/me/profile/signature")).data;
      queryClient.setQueryData(["my-profile"], updated);
      setBanner({ kind: "info", text: "Signature removed." });
    } catch {
      setBanner({ kind: "error", text: "Could not remove the signature." });
    } finally {
      setSigBusy(false);
    }
  };

  if (isLoading) {
    return (
      <div style={{ padding: 24, color: "var(--mute)" }}>Loading profile…</div>
    );
  }

  return (
    <div style={{ maxWidth: 720, margin: "32px auto", padding: 24 }}>
      <header style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 28, fontWeight: 600, letterSpacing: "-0.3px", margin: 0 }}>
          {mode === "onboard" ? "Complete your profile" : "Your profile"}
        </h1>
        <p style={{ color: "var(--mute)", fontSize: 13, marginTop: 6 }}>
          {mode === "onboard"
            ? "A one-time step before you can use the workspace. You can edit any of this later from Settings → Profile."
            : "Edit your details. Visible to you, your tenant admin, and the platform owners."}
        </p>
      </header>

      {banner && (
        <div
          role="alert"
          style={{
            padding: "10px 14px",
            borderRadius: 4,
            marginBottom: 16,
            fontSize: 13,
            color: banner.kind === "error" ? "#ee1d36" : "#047a18",
            background:
              banner.kind === "error"
                ? "rgba(238,29,54,0.06)"
                : "rgba(0,215,34,0.06)",
            border:
              banner.kind === "error"
                ? "1px solid rgba(238,29,54,0.3)"
                : "1px solid rgba(0,215,34,0.3)",
          }}
        >
          {banner.text}
        </div>
      )}

      {/* Required */}
      <section style={card}>
        <div style={sectionTitle}>Contact · required</div>
        {profile?.job_id && (
          <div style={fieldRow}>
            <span style={fieldLabel}>Job ID</span>
            <div
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 13,
                color: "var(--ink)",
                padding: "4px 0",
              }}
            >
              {profile.job_id}
              <span style={{ color: "var(--mute)", marginLeft: 8, fontSize: 11 }}>
                set by your workspace admin
              </span>
            </div>
          </div>
        )}
        {profile?.designation && (
          <div style={fieldRow}>
            <span style={fieldLabel}>Designation</span>
            <div
              style={{
                fontSize: 13,
                color: "var(--ink)",
                padding: "4px 0",
              }}
            >
              {profile.designation}
              <span style={{ color: "var(--mute)", marginLeft: 8, fontSize: 11 }}>
                set by your workspace admin
              </span>
            </div>
          </div>
        )}
        <label style={fieldRow}>
          <span style={fieldLabel}>Phone</span>
          <input
            type="tel"
            placeholder="+1 415 555 0188"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            style={input}
          />
        </label>
        <label style={fieldRow}>
          <span style={fieldLabel}>Address</span>
          <textarea
            placeholder="Street, City, State, ZIP"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            style={{ ...input, minHeight: 64, resize: "vertical", fontFamily: "var(--font-sans)" }}
          />
        </label>
      </section>

      <section style={card}>
        <div style={sectionTitle}>Resume · PDF or Word (.pdf / .doc / .docx)</div>
        {profile?.resume_filename ? (
          <div style={resumeChip}>
            <div>
              <strong>{profile.resume_filename}</strong>
              <span style={{ color: "var(--mute)", fontSize: 12, marginLeft: 8 }}>
                {profile.resume_size_bytes
                  ? `${(profile.resume_size_bytes / 1024).toFixed(1)} KB`
                  : ""}
                {profile.resume_uploaded_at
                  ? ` · uploaded ${new Date(profile.resume_uploaded_at).toLocaleDateString()}`
                  : ""}
              </span>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              {profile.resume_download_url && (
                <a
                  href={profile.resume_download_url}
                  target="_blank"
                  rel="noreferrer"
                  style={ghostBtn}
                >
                  Download
                </a>
              )}
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                style={ghostBtn}
              >
                Replace
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            style={{
              ...primaryBtn,
              opacity: uploading ? 0.6 : 1,
              cursor: uploading ? "wait" : "pointer",
            }}
          >
            {uploading ? "Uploading…" : "Upload resume"}
          </button>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.doc,.docx,.docm,.dotx,.dotm,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-word.document.macroEnabled.12,application/vnd.openxmlformats-officedocument.wordprocessingml.template,application/vnd.ms-word.template.macroEnabled.12"
          style={{ display: "none" }}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void onResumeChosen(f);
          }}
        />
      </section>

      <section style={card}>
        <div style={sectionTitle}>Signature · PNG, JPG, or WebP (optional)</div>
        <p style={{ fontSize: 12.5, color: "var(--mute)", margin: "0 0 12px", lineHeight: 1.5 }}>
          Upload a picture of your signature. Used as your signatory mark on
          documents such as timesheet exports.
        </p>
        {profile?.signature_uploaded ? (
          <div style={resumeChip}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              {profile.signature_download_url && (
                <img
                  src={profile.signature_download_url}
                  alt="Your signature"
                  style={{
                    height: 44,
                    maxWidth: 160,
                    objectFit: "contain",
                    background: "var(--canvas)",
                    border: "1px solid var(--hairline)",
                    borderRadius: 4,
                    padding: 4,
                  }}
                />
              )}
              <span style={{ color: "var(--mute)", fontSize: 12 }}>
                {profile.signature_uploaded_at
                  ? `uploaded ${new Date(profile.signature_uploaded_at).toLocaleDateString()}`
                  : "uploaded"}
              </span>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button
                type="button"
                onClick={() => sigInputRef.current?.click()}
                disabled={sigBusy}
                style={ghostBtn}
              >
                Replace
              </button>
              <button
                type="button"
                onClick={() => void onSignatureRemove()}
                disabled={sigBusy}
                style={ghostBtn}
              >
                Remove
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => sigInputRef.current?.click()}
            disabled={sigBusy}
            style={{
              ...primaryBtn,
              opacity: sigBusy ? 0.6 : 1,
              cursor: sigBusy ? "wait" : "pointer",
            }}
          >
            {sigBusy ? "Uploading…" : "Upload signature"}
          </button>
        )}
        <input
          ref={sigInputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,.png,.jpg,.jpeg,.webp"
          style={{ display: "none" }}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void onSignatureChosen(f);
          }}
        />
      </section>

      {/* Optional but encouraged */}
      <section style={card}>
        <div style={sectionTitle}>Work authorisation · optional</div>
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 12 }}>
          <label style={fieldRow}>
            <span style={fieldLabel}>Visa status</span>
            <select
              value={visaStatus}
              onChange={(e) => setVisaStatus(e.target.value)}
              style={input}
            >
              <option value="">— Select —</option>
              {VISA_OPTIONS.map((v) => (
                <option key={v.code} value={v.code}>
                  {v.label}
                </option>
              ))}
            </select>
          </label>
          <label style={fieldRow}>
            <span style={fieldLabel}>Expiry (if applicable)</span>
            <input
              type="date"
              value={visaExpiry}
              onChange={(e) => setVisaExpiry(e.target.value)}
              style={input}
            />
          </label>
        </div>
      </section>

      <section style={card}>
        <div style={sectionTitle}>Professional · optional</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <label style={fieldRow}>
            <span style={fieldLabel}>LinkedIn URL</span>
            <input
              type="url"
              placeholder="https://www.linkedin.com/in/…"
              value={linkedinUrl}
              onChange={(e) => setLinkedinUrl(e.target.value)}
              style={input}
            />
          </label>
          <label style={fieldRow}>
            <span style={fieldLabel}>GitHub URL</span>
            <input
              type="url"
              placeholder="https://github.com/…"
              value={githubUrl}
              onChange={(e) => setGithubUrl(e.target.value)}
              style={input}
            />
          </label>
        </div>
        <label style={fieldRow}>
          <span style={fieldLabel}>Skills (comma-separated)</span>
          <input
            type="text"
            placeholder="Python, React, AWS, system design"
            value={skillsText}
            onChange={(e) => setSkillsText(e.target.value)}
            style={input}
          />
        </label>
        <label style={fieldRow}>
          <span style={fieldLabel}>Years of experience</span>
          <input
            type="number"
            min={0}
            max={80}
            placeholder="6"
            value={yearsExperience}
            onChange={(e) => setYearsExperience(e.target.value)}
            style={{ ...input, maxWidth: 140 }}
          />
        </label>
      </section>

      <section style={card}>
        <div style={sectionTitle}>Personal · optional</div>
        <label style={fieldRow}>
          <span style={fieldLabel}>Date of birth</span>
          <input
            type="date"
            value={dateOfBirth}
            onChange={(e) => setDateOfBirth(e.target.value)}
            style={{ ...input, maxWidth: 220 }}
          />
        </label>

        <div style={subsection}>Emergency contact</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
          <label style={fieldRow}>
            <span style={fieldLabel}>Name</span>
            <input
              type="text"
              placeholder="Alex Doe"
              value={emergencyName}
              onChange={(e) => setEmergencyName(e.target.value)}
              style={input}
            />
          </label>
          <label style={fieldRow}>
            <span style={fieldLabel}>Relation</span>
            <input
              type="text"
              placeholder="Spouse"
              value={emergencyRelation}
              onChange={(e) => setEmergencyRelation(e.target.value)}
              style={input}
            />
          </label>
          <label style={fieldRow}>
            <span style={fieldLabel}>Phone</span>
            <input
              type="tel"
              placeholder="+1 415 555 0123"
              value={emergencyPhone}
              onChange={(e) => setEmergencyPhone(e.target.value)}
              style={input}
            />
          </label>
        </div>
      </section>

      <section style={card}>
        <div style={sectionTitle}>Staffing · optional</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
          <label style={fieldRow}>
            <span style={fieldLabel}>Hourly rate (USD)</span>
            <input
              type="number"
              min={0}
              step="0.01"
              placeholder="125"
              value={hourlyRate}
              onChange={(e) => setHourlyRate(e.target.value)}
              style={input}
            />
          </label>
          <label style={fieldRow}>
            <span style={fieldLabel}>Salary expectation (USD)</span>
            <input
              type="number"
              min={0}
              step="0.01"
              placeholder="180000"
              value={salaryExpectation}
              onChange={(e) => setSalaryExpectation(e.target.value)}
              style={input}
            />
          </label>
          <label style={fieldRow}>
            <span style={fieldLabel}>Notice period (days)</span>
            <input
              type="number"
              min={0}
              max={365}
              placeholder="14"
              value={noticeDays}
              onChange={(e) => setNoticeDays(e.target.value)}
              style={input}
            />
          </label>
        </div>
      </section>

      <div style={{ display: "flex", gap: 12, marginTop: 24 }}>
        <button
          type="button"
          onClick={onSubmit}
          disabled={save.isPending || uploading}
          style={{
            ...primaryBtn,
            flex: 1,
            opacity: save.isPending || uploading ? 0.6 : 1,
            cursor: save.isPending || uploading ? "wait" : "pointer",
          }}
        >
          {save.isPending ? "Saving…" : "Save and continue"}
        </button>
      </div>
    </div>
  );
}

// ---------- styles ----------

const card: CSSProperties = {
  background: "var(--canvas)",
  border: "1px solid var(--hairline)",
  borderRadius: 8,
  padding: 20,
  marginBottom: 16,
};

const sectionTitle: CSSProperties = {
  fontSize: 11,
  fontWeight: 500,
  letterSpacing: 1.2,
  textTransform: "uppercase",
  color: "var(--mute)",
  marginBottom: 12,
};

const subsection: CSSProperties = {
  fontSize: 12,
  fontWeight: 500,
  color: "var(--body-mid)",
  marginTop: 8,
  marginBottom: 8,
};

const fieldRow: CSSProperties = { display: "block", marginBottom: 12 };

const fieldLabel: CSSProperties = {
  display: "block",
  fontSize: 11,
  fontWeight: 500,
  letterSpacing: 0.4,
  textTransform: "uppercase",
  color: "var(--mute)",
  marginBottom: 4,
};

const input: CSSProperties = {
  width: "100%",
  background: "var(--canvas)",
  border: "1px solid var(--hairline)",
  borderRadius: 4,
  padding: "8px 10px",
  fontFamily: "var(--font-sans)",
  fontSize: 14,
  color: "var(--ink)",
  outline: "none",
};

const primaryBtn: CSSProperties = {
  background: "var(--ink)",
  color: "var(--on-primary)",
  border: "none",
  borderRadius: 4,
  padding: "10px 16px",
  fontSize: 13,
  fontWeight: 500,
  cursor: "pointer",
};

const ghostBtn: CSSProperties = {
  background: "var(--canvas)",
  color: "var(--ink)",
  border: "1px solid var(--hairline)",
  borderRadius: 4,
  padding: "6px 12px",
  fontSize: 12,
  cursor: "pointer",
  textDecoration: "none",
};

const resumeChip: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  background: "var(--surface-1)",
  border: "1px solid var(--hairline)",
  borderRadius: 4,
  padding: "10px 14px",
  fontSize: 13,
};
